import React, { useState, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { message } from 'antd';
import HazardIndexEdit from '../HazardIndexEdit/HazardIndexEdit';
import RiskCheckListEdit from '../RiskCheckListEdit/RiskCheckListEdit';
import { industryIdSelector } from '../../../Industry/IndustrySelectors';
import { underwriterHazardIndexCustomizationSelector } from '../../ReportSelectors';
import { updateReportCustomizations } from '../../ReportActions';
import { buildUnderwriterChecklistQuestionsToSave } from '../../utils/underwriterMergeSave';
import './UnderwriterEdit.scss';

const UnderwriterEdit = ({
  hazardIndex,
  close,
  comment,
  answers,
  industryId,
  customization,
  riskChecklist,
  loadingCustomization,
  updateReportCustomizations: persistCustomization,
}) => {
  const [tab, setTab] = useState('hazardIndex');

  const hazardFormRef = useRef(null);
  const checklistFormRef = useRef(null);
  const checklistCheckedListRef = useRef([]);

  const syncChecklistState = useCallback((list) => {
    checklistCheckedListRef.current = list;
  }, []);

  const connectHazardForm = useCallback((form) => {
    hazardFormRef.current = form;
  }, []);

  const connectChecklistForm = useCallback((form) => {
    checklistFormRef.current = form;
  }, []);

  const saveCommentsAndChecklist = useCallback(() => {
    const hf = hazardFormRef.current;
    const rf = checklistFormRef.current;

    if (!industryId) {
      message.warn('Unable to save — industry is not loaded.');
      return;
    }

    /** `report_id` can be omitted on first POST; API creates the draft and Redux receives `id` from the response */
    const reportDraftId = customization?.id

    if (loadingCustomization) {
      message.warn('A save is still in progress. Wait a moment and try again.')
      return
    }

    if (!hf || !rf) {
      message.warn('Unable to save — forms are still connecting. Switch between Comments and Risk checklist, then retry.');
      return;
    }

    hf.validateFields((errH, hazardValues) => {
      if (errH) {
        message.error('Unable to save — check the Comments tab for errors.')
        return
      }

      rf.validateFields(async (errR, riskValues) => {
        if (errR) {
          message.error('Unable to save — check the Risk checklist tab for incomplete fields.')
          return
        }

        const checkedListNow = checklistCheckedListRef.current || [];
        const questionsToSave = buildUnderwriterChecklistQuestionsToSave(
          riskValues,
          checkedListNow,
          riskChecklist
        );

        const customizationsBaseline = customization?.customizations ?? []
        const classToUpdate = customizationsBaseline.find((c) => c.id === +hazardIndex.id)
        const customizations = customizationsBaseline.filter((c) => c.id !== +hazardIndex.id)

        try {
          await persistCustomization(
            industryId,
            reportDraftId,
            customization?.name ?? 'Underwriter report',
            [
              {
                section: 'hazard_index_underwriter',
                customization: JSON.stringify({
                  classes: [
                    ...customizations,
                    {
                      ...classToUpdate,
                      id: +hazardIndex.id,
                      comments: hazardValues.comment,
                      checklist: questionsToSave,
                    },
                  ],
                }),
              },
            ],
          );
          message.success('Saved');
          close();
        } catch (err) {
          message.error(err.message);
        }
      });
    });
  }, [
    hazardIndex,
    industryId,
    customization,
    riskChecklist,
    persistCustomization,
    close,
    loadingCustomization,
  ]);

  const handleNext = () => {
    setTab('riskChecklist');
  };

  const handleBack = () => {
    setTab('hazardIndex');
  };

  return (
    <div className="underwriter-edit-modal">
      <div className="underwriter-edit-tabs" role="tablist" aria-label="Comment sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'hazardIndex'}
          className={classNames('underwriter-edit-tab', { 'is-active': tab === 'hazardIndex' })}
          onClick={() => setTab('hazardIndex')}
        >
          Comments
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'riskChecklist'}
          className={classNames('underwriter-edit-tab', { 'is-active': tab === 'riskChecklist' })}
          onClick={() => setTab('riskChecklist')}
        >
          Risk checklist
        </button>
      </div>
      <div className="underwriter-edit-body">
        <div
          className={classNames('underwriter-edit-panel', { 'is-visible': tab === 'hazardIndex' })}
          aria-hidden={tab !== 'hazardIndex'}
        >
          <HazardIndexEdit
            hazardIndex={hazardIndex}
            close={close}
            comment={comment}
            onAddChecklist={handleNext}
            underwriter
            connectHazardForm={connectHazardForm}
            onMergedUnderwriterSave={saveCommentsAndChecklist}
            mergeSaveDisabled={loadingCustomization}
          />
        </div>
        <div
          className={classNames('underwriter-edit-panel', { 'is-visible': tab === 'riskChecklist' })}
          aria-hidden={tab !== 'riskChecklist'}
        >
          <RiskCheckListEdit
            hazardIndex={hazardIndex}
            answers={answers}
            onUnderwriterComments={handleBack}
            connectChecklistForm={connectChecklistForm}
            syncChecklistState={syncChecklistState}
            onMergedUnderwriterSave={saveCommentsAndChecklist}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  industryId: industryIdSelector(state),
  customization: underwriterHazardIndexCustomizationSelector(state),
  riskChecklist: state.industry.riskChecklist || [],
  loadingCustomization: state.report.loadingCustomizations,
});

const mapDispatchToProps = {
  updateReportCustomizations,
};

export default connect(mapStateToProps, mapDispatchToProps)(UnderwriterEdit);
